import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Grid,
  CircularProgress,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Search,
  Clear,
  Refresh,
} from '@mui/icons-material';
import { fetchAffiliateReferrals } from '../../services/affiliate';
import { useTheme } from '@mui/material/styles';

const AffiliateReferralsPage = () => {
  const { t } = useTranslation(['affiliate', 'common']);
  const theme = useTheme();
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Pagination
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);

  const fetchReferrals = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1, // Backend uses 1-based pagination
        page_size: pageSize,
      };
      
      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }
      
      const response = await fetchAffiliateReferrals(params);
      setReferrals(response.results || []);
      setTotalCount(response.count || 0);
    } catch (error) {
      console.error('Error fetching referrals:', error);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchQuery]);

  useEffect(() => {
    fetchReferrals();
  }, [fetchReferrals]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setPage(0); // Reset to first page on search
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setPage(0);
  };

  const columns = useMemo(() => [
    {
      field: 'id',
      headerName: 'ID',
      width: 80,
      sortable: false,
    },
    {
      field: 'affiliate_code',
      headerName: 'Affiliate Code',
      width: 150,
      sortable: false,
    },
    {
      field: 'affiliate_name',
      headerName: 'Affiliate Name',
      width: 200,
      sortable: false,
    },
    {
      field: 'referral_user',
      headerName: 'Referral User',
      width: 200,
      sortable: false,
      valueGetter: (value, row) => {
        const data = row || value;
        if (!data) return 'N/A';
        const user = data.invited_user;
        if (!user) return 'N/A';
        return user.fullname || user.username || user.email || 'N/A';
      },
    },
    {
      field: 'referral_email',
      headerName: 'Email',
      width: 220,
      sortable: false,
      valueGetter: (value, row) => {
        const data = row || value;
        if (!data) return 'N/A';
        const user = data.invited_user;
        return user?.email || data.invited_email || 'N/A';
      },
    },
    {
      field: 'referral_phone',
      headerName: 'Phone',
      width: 150,
      sortable: false,
      valueGetter: (value, row) => {
        const data = row || value;
        if (!data) return 'N/A';
        const user = data.invited_user;
        return user?.phone || 'N/A';
      },
    },
    {
      field: 'referral_username',
      headerName: 'Username',
      width: 150,
      sortable: false,
      valueGetter: (value, row) => {
        const data = row || value;
        if (!data) return 'N/A';
        const user = data.invited_user;
        return user?.username || 'N/A';
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      sortable: false,
      renderCell: (params) => {
        if (!params?.row) return 'N/A';
        const status = params.row.status || 'N/A';
        const statusConfig = {
          active: { bg: '#4CAF50', color: '#FFFFFF', label: 'Active' },
          pending: { bg: '#FF9800', color: '#FFFFFF', label: 'Pending' },
          expired: { bg: '#F44336', color: '#FFFFFF', label: 'Expired' },
        };
        const config = statusConfig[status] || { bg: '#9E9E9E', color: '#FFFFFF', label: status };
        return (
          <Box
            sx={{
              px: 1.5,
              py: 0.5,
              borderRadius: 1,
              backgroundColor: config.bg,
              color: config.color,
              fontWeight: '600',
              fontSize: '0.75rem',
              textTransform: 'capitalize',
              display: 'inline-block',
            }}
          >
            {config.label}
          </Box>
        );
      },
    },
    {
      field: 'invited_at',
      headerName: 'Invited At',
      width: 180,
      sortable: false,
      valueGetter: (value, row) => {
        const data = row || value;
        if (!data?.invited_at) return 'N/A';
        try {
          return new Date(data.invited_at).toLocaleString();
        } catch (e) {
          return 'N/A';
        }
      },
    },
    {
      field: 'expires_at',
      headerName: 'Expires At',
      width: 180,
      sortable: false,
      valueGetter: (value, row) => {
        const data = row || value;
        if (!data?.expires_at) return 'N/A';
        try {
          return new Date(data.expires_at).toLocaleString();
        } catch (e) {
          return 'N/A';
        }
      },
    },
  ], []);

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            {t('affiliate:referrals.title', 'Affiliate Referrals')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('affiliate:referrals.description', 'View and manage all affiliate referrals')}
          </Typography>
        </Box>
        <Button 
          variant="outlined" 
          startIcon={<Refresh />} 
          onClick={fetchReferrals}
          disabled={loading}
        >
          {t('common:refresh')}
        </Button>
      </Box>

      {/* Search Filter */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              placeholder={t('affiliate:referrals.searchPlaceholder', 'Search by affiliate code, email, phone, or username')}
              value={searchQuery}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleClearSearch}>
                      <Clear fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* DataGrid */}
      <Paper sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={referrals}
          columns={columns}
          loading={loading}
          paginationMode="server"
          page={page}
          pageSize={pageSize}
          rowCount={totalCount}
          onPageChange={(newPage) => setPage(newPage)}
          onPageSizeChange={(newPageSize) => {
            setPageSize(newPageSize);
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50, 100]}
          disableSelectionOnClick
          sx={{
            '& .MuiDataGrid-columnHeader': {
              backgroundColor: theme.palette.primary.main,
              color: 'white',
              fontWeight: 'bold',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 'bold',
              color: 'white',
            },
            '& .MuiDataGrid-columnHeader:focus': {
              outline: 'none',
            },
            '& .MuiDataGrid-columnHeader:focus-within': {
              outline: 'none',
            },
            '& .MuiDataGrid-cell:focus': {
              outline: 'none',
            },
            '& .MuiDataGrid-cell:focus-within': {
              outline: 'none',
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: '1px solid rgba(224, 224, 224, 1)',
            },
          }}
        />
      </Paper>
    </Box>
  );
};

export default AffiliateReferralsPage;
